<template>
  <div class="h5-page">
    <div class="start-overlay" v-if="!started" @click="handleStart">
      <div class="start-content">
        <div class="start-icon">▶</div>
        <div class="start-text">点击体验</div>
      </div>
    </div>

    <div id="video-container" class="video-container"></div>

    <div class="status-indicator" v-if="statusMessage">
      {{ statusMessage }}
    </div>

    <div class="chat-overlay" ref="chatOverlay">
      <div class="chat-messages">
        <div
          v-for="(message, index) in chatMessages"
          :key="index"
          class="chat-bubble"
          :class="{
            'system-bubble': message.role === 'system',
            'user-bubble': message.role === 'user',
            'ai-bubble': message.role === 'ai'
          }"
        >
          <span class="bubble-text" v-html="renderMarkdown(message.content)"></span>
          <span
            v-if="isStreaming && index === chatMessages.length - 1 && message.role === 'ai'"
            class="streaming-cursor"
          >|</span>
        </div>
      </div>
    </div>

    <div class="chat-input-btn" v-if="!showInputDialog && started" @click="openInputDialog">
      <span class="chat-input-btn-icon">💬</span>
    </div>

    <div class="input-dialog-mask" v-if="showInputDialog" @click.self="closeInputDialog">
      <div class="input-dialog">
        <div class="input-dialog-container">
          <input
            ref="dialogInput"
            v-model="userInput"
            type="text"
            :placeholder="isStreaming ? 'AI正在思考...' : '请输入消息'"
            :disabled="isStreaming"
            @keyup.enter="handleSendMessage"
          />
          <button
            @click="handleSendMessage"
            :disabled="isStreaming || !userInput.trim()"
          >
            {{ isStreaming ? '发送中...' : '发送' }}
          </button>
        </div>
      </div>
    </div>

    <div class="toast" v-if="showToast">
      <div class="toast-content">
        {{ toastMessage }}
      </div>
    </div>

</div>
</template>

<script>
import { SzrPlayer } from "@/assets/szrSdk.js";
import { marked } from "marked";

export default {
  name: "H5Page",
  data() {
    return {
      player: null,
      statusMessage: '加载中...',
      chatMessages: [],
      userInput: '',
      isStreaming: false,
      showToast: false,
      toastMessage: '',
      toastTimer: null,
      welcomeSent: false,
      started: false,
      showInputDialog: false,
      chatId: '',
      // --- 数字人播报缓冲区 ---
      // Agent 流式返回的 token 较碎（可能是单个字或词），不适合直接逐 token 发送给数字人 TTS。
      // 因此先累积到 sentenceBuffer，按句子边界切分后再发送，以获得更自然的语音播报效果。
      sentenceBuffer: '',
      // 标记当前是否是新一轮播报的第一个分片（用于设置 end_flag: 0 vs 1）
      sentenceFirstChunk: true,
      // 数字人配置
      config: {
        apiBaseUrl: "https://szrstream.smart-xiaoduo.cn:64441/api/v1/live",
        concurrencyId: "ccxy20e09aa4f5a5b878",
        avatarId: 10002,
        ttsId: "S_9klUxIJj1",
        appId: "xy623f6b80f0b0499f",
        env: process.env.VUE_APP_SZR_ENV || 'dev',
        authorizeId: "1489b164wqfsad68f3d592264bc1185"
      },
    };
  },
  created() {
    window.addEventListener("beforeunload", this.handleBeforeUnload);
    this.chatId = `h5_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  },
  mounted() {
  },
  beforeDestroy() {
    window.removeEventListener("beforeunload", this.handleBeforeUnload);
    if (this.player) {
      this.player.destroy();
    }
    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
    }
  },
  methods: {
    handleStart() {
      this.started = true;
      this.initDigitalHuman();
    },

    initDigitalHuman() {
      console.log("初始化数字人播放器");
      const params = {
        containerId: "video-container", // 容器id
        concurrencyId: this.config.concurrencyId, // 并发id
        avatarId: this.config.avatarId, // 数字人id
        ttsId: this.config.ttsId, // 数字人声音
        appId: this.config.appId, // 应用id
        env: this.config.env, //环境变量
        apiBaseUrl: this.config.apiBaseUrl, // 使用配置的API地址
        authorizeId: this.config.authorizeId, // 授权id
        isWsAsk: true, //开启流式输入
        // askWsUrl: 'ws://36.103.199.24:64440/api/v1/live/ws'
        askWsUrl: 'wss://szrstream.smart-xiaoduo.cn:64441/api/v1/live/ws',
      };

      this.player = new SzrPlayer(params);

      this.player.subscribe("streamStatus", (status) => {
        console.log("数字人状态更新:", status);
        if (status === "1") {
          if (!this.welcomeSent) {
            console.log("就绪");
            this.statusMessage = '';
            this.handleStreamReady();
          } else {
            this.statusMessage = "初始化完成";
          }
        } else if (status === "2") {
          console.log("正在说话");
          this.statusMessage = '正在说话...';
        }else if (status === "-1") {
          console.log("就绪");
          this.statusMessage = '';
        }
      });

      this.player.subscribe("errorLog", () => {
        this.showToastMessage('数字人连接异常，请稍后重试');
      });
    },

    handleStreamReady() {
      if (this.welcomeSent) return;
      this.welcomeSent = true;

      this.chatMessages.push({
        role: 'system',
        content: '广旅数字人欢迎您'
      });

      // 播报欢迎语
      // ：end_flag=0 表示语音段开始，end_flag=2 表示语音段结束
      this.player.sendMessage('广旅数字人欢迎您');
     
      this.scrollChatToBottom();
    },

    /**
     * 将 Agent 流式返回的 token 追加到缓冲区，并尝试按句子边界切分后发送给数字人播报。
     * 调用链：callAgentAPI 收到 SSE token → bufferAppend → bufferTryFlush → bufferSendChunk → player.sendAskMessage
     */
    bufferAppend(token) {
      this.sentenceBuffer += token;
      this.bufferTryFlush();
    },

    /**
     * 句子分片策略：将累积的 token 按语义边界切分后发送给数字人 TTS。
     * 优先级：
     *   1. 句末标点（。！？；换行）→ 立即切分发送
     *   2. 缓冲区超 20 字且无句末标点 → 在最后一个逗号处切分
     *   3. 无逗号 → 强制在 20 字处截断发送
     */
    bufferTryFlush() {
      const sentenceEndChars = '。！？；\n';
      let lastSentenceEnd = -1;

      for (let i = 0; i < this.sentenceBuffer.length; i++) {
        if (sentenceEndChars.includes(this.sentenceBuffer[i])) {
          lastSentenceEnd = i;
        }
      }

      if (lastSentenceEnd !== -1) {
        const sentence = this.sentenceBuffer.substring(0, lastSentenceEnd + 1);
        this.bufferSendChunk(sentence);
        this.sentenceBuffer = this.sentenceBuffer.substring(lastSentenceEnd + 1);
      } else if (this.sentenceBuffer.length > 20) {
        const commaIndex = this.sentenceBuffer.lastIndexOf('，');
        const commaIndexEn = this.sentenceBuffer.lastIndexOf(',');
        const cutAt = Math.max(commaIndex, commaIndexEn);
        if (cutAt !== -1) {
          this.bufferSendChunk(this.sentenceBuffer.substring(0, cutAt));
          this.sentenceBuffer = this.sentenceBuffer.substring(cutAt + 1);
        } else {
          this.bufferSendChunk(this.sentenceBuffer.substring(0, 20));
          this.sentenceBuffer = this.sentenceBuffer.substring(20);
        }
      }
    },

    /**
     * 向数字人发送一个语音分片（通过 WebSocket）。
     * end_flag 协议：
     *   0 = 新语音段的第一个分片（通知数字人开始新的 TTS 合成）
     *   1 = 语音段续接分片（继续拼接当前语音段）
     *   2 = 语音段结束（在 bufferFlush 中使用）
     */
    bufferSendChunk(text) {
      if (!text.trim()) return;
      const plainText = this.stripMarkdown(text);
      if (!plainText) return;
      const end_flag = this.sentenceFirstChunk ? 0 : 1;
      console.log(`发送语音分片 (end_flag=${end_flag}):`, plainText);
      this.player.sendAskMessage(JSON.stringify({
        end_flag,
        Content: plainText
      }));
      this.sentenceFirstChunk = false;
    },

    /**
     * 刷新缓冲区剩余内容：在 Agent 流式响应结束时调用。
     * 将缓冲区中未发送的文本以 end_flag=2（语音段结束）发送给数字人。
     * 如果缓冲区为空但已有分片发出过，仍需发送空内容的 end_flag=2 来通知数字人语音段结束。
     */
    bufferFlush() {
      console.log("刷新播报缓冲区，剩余文本:", this.sentenceBuffer);
      if (this.sentenceBuffer.trim()) {
        this.player.sendAskMessage(JSON.stringify({
          end_flag: 2,
          Content: this.sentenceBuffer
        }));
      } else if (!this.sentenceFirstChunk) {
        this.player.sendAskMessage(JSON.stringify({
          end_flag: 2,
          Content: ''
        }));
      }
      this.sentenceBuffer = '';
      this.sentenceFirstChunk = true;
    },

    openInputDialog() {
      if (this.isStreaming) return;
      this.showInputDialog = true;
      this.$nextTick(() => {
        if (this.$refs.dialogInput) {
          this.$refs.dialogInput.focus();
        }
      });
    },

    closeInputDialog() {
      this.showInputDialog = false;
    },

    handleSendMessage() {
      const message = this.userInput.trim();
      if (!message || this.isStreaming) return;

      this.chatMessages.push({ role: 'user', content: message });
      this.chatMessages.push({ role: 'ai', content: '' });
      this.userInput = '';
      this.showInputDialog = false;
      this.isStreaming = true;
      this.scrollChatToBottom();
      this.callAgentAPI(message);
    },

    async callAgentAPI(message) {
      const url = '/agent-api/v1/chat/completions';

      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            chatId: this.chatId,
            stream: true,
            detail: false,
            messages: [{ role: 'user', content: message }]
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let sseBuffer = '';
        let reading = true;

        while (reading) {
          const { done, value } = await reader.read();
          if (done) { reading = false; break; }

          sseBuffer += decoder.decode(value, { stream: true });
          const lines = sseBuffer.split('\n');
          sseBuffer = lines.pop();

          for (const line of lines) {
            const trimmed = line.trim();
            if (!trimmed || !trimmed.startsWith('data:')) continue;

            const data = trimmed.slice(5).trim();
            if (data === '[DONE]') {
              this.handleStreamComplete();
              return;
            }

            try {
              const json = JSON.parse(data);

              // 检测错误响应（有 code/statusText 但无 choices）
              if (json.code !== undefined && !json.choices) {
                const errMsg = json.statusText || json.message || '未知错误';
                console.error('Agent API error:', json.code, errMsg);
                this.showToastMessage(`AI 服务异常 (${json.code}): ${errMsg}`);
                this.handleStreamError();
                return;
              }

              const content = json.choices?.[0]?.delta?.content;
              if (content) {
                // 更新聊天气泡显示
                const lastIdx = this.chatMessages.length - 1;
                this.chatMessages[lastIdx].content += content;
                // 将 token 送入播报缓冲区，按句子切分后流式发送给数字人播报
                this.bufferAppend(content);
                this.scrollChatToBottom();
              }
            } catch (e) {
              // skip unparseable lines
            }
          }
        }

        // Stream ended without [DONE] — still flush
        this.handleStreamComplete();
      } catch (error) {
        console.error('Agent API error:', error);
        this.showToastMessage('网络连接异常，请重试');
        this.sentenceBuffer = '';
        this.sentenceFirstChunk = true;
        this.isStreaming = false;
        const lastIdx = this.chatMessages.length - 1;
        if (lastIdx >= 0 && this.chatMessages[lastIdx].role === 'ai' && !this.chatMessages[lastIdx].content) {
          this.chatMessages.splice(lastIdx, 1);
        }
      }
    },

    // Agent SSE 流正常结束：刷新缓冲区，将剩余文本发送给数字人播报
    handleStreamComplete() {
      this.bufferFlush();
      this.isStreaming = false;
      this.scrollChatToBottom();
    },

    handleStreamError() {
      this.sentenceBuffer = '';
      this.sentenceFirstChunk = true;
      this.isStreaming = false;
      // 移除空的 AI 气泡
      const lastIdx = this.chatMessages.length - 1;
      if (lastIdx >= 0 && this.chatMessages[lastIdx].role === 'ai' && !this.chatMessages[lastIdx].content) {
        this.chatMessages.splice(lastIdx, 1);
      }
      this.scrollChatToBottom();
    },

    handleBeforeUnload() {
      if (this.player) {
        this.player.destroy();
      }
    },

    showToastMessage(message) {
      this.toastMessage = message;
      this.showToast = true;
      if (this.toastTimer) {
        clearTimeout(this.toastTimer);
      }
      this.toastTimer = setTimeout(() => {
        this.showToast = false;
      }, 3000);
    },

    renderMarkdown(content) {
      if (!content) return '';
      return marked.parse(content);
    },

    stripMarkdown(text) {
      if (!text) return text;
      return text
        .replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1')
        .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
        .replace(/(\*\*|__)(.*?)\1/g, '$2')
        .replace(/(\*|_)(.*?)\1/g, '$2')
        .replace(/^#{1,6}\s+/gm, '')
        .replace(/`([^`]+)`/g, '$1')
        .replace(/^>\s+/gm, '')
        .replace(/^[-*]\s+/gm, '')
        .replace(/^\d+\.\s+/gm, '')
        .trim();
    },

    scrollChatToBottom() {
      this.$nextTick(() => {
        const el = this.$refs.chatOverlay;
        if (el) {
          el.scrollTop = el.scrollHeight;
        }
      });
    }
  }
};
</script>

<style scoped>
.start-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 100;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
}

.start-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 36px;
}

.start-icon {
  width: 140px;
  height: 140px;
  border-radius: 50%;
  background-color: rgba(24, 144, 255, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 56px;
  color: #fff;
  transition: transform 0.2s;
}

.start-overlay:active .start-icon {
  transform: scale(0.9);
}

.start-text {
  color: #fff;
  font-size: 32px;
  letter-spacing: 4px;
}

.h5-page {
  position: relative;
  width: 100vw;
  height: 100vh;
  margin: 0;
  padding: 0;
  overflow: hidden;
  background-color: #e3eefa;
}

.video-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
}

.status-indicator {
  position: absolute;
  top: 36px;
  left: 36px;
  z-index: 10;
  color: #fff;
  font-size: 22px;
  background-color: rgba(0, 0, 0, 0.5);
  padding: 10px 22px;
  border-radius: 28px;
}

.chat-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 40%;
  z-index: 20;
  padding: 20px 24px;
  overflow-y: auto;
  pointer-events: none;
  -webkit-overflow-scrolling: touch;
  box-sizing: border-box;
}

.chat-messages {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  min-height: 100%;
  gap: 12px;
  padding-bottom: 12px;
}

.chat-bubble {
  max-width: 70%;
  padding: 14px 20px;
  border-radius: 16px;
  word-break: break-all;
  pointer-events: auto;
  animation: fadeIn 0.3s ease-in;
  font-size: 240%;
  line-height: 1.5;
}

.system-bubble,
.ai-bubble {
  align-self: flex-start;
  background-color: rgba(0, 0, 0, 0.5);
  color: #fff;
}

.ai-bubble {
  word-break: break-word;
}

.ai-bubble ::v-deep p {
  margin: 0 0 0.2em;
}

.ai-bubble ::v-deep p:last-child {
  margin-bottom: 0;
}

.ai-bubble ::v-deep h1,
.ai-bubble ::v-deep h2,
.ai-bubble ::v-deep h3,
.ai-bubble ::v-deep h4,
.ai-bubble ::v-deep h5,
.ai-bubble ::v-deep h6 {
  color: #fff;
  font-weight: bold;
  line-height: 1.3;
  margin: 0.3em 0 0.15em;
}

.ai-bubble ::v-deep h1:first-child,
.ai-bubble ::v-deep h2:first-child,
.ai-bubble ::v-deep h3:first-child,
.ai-bubble ::v-deep h4:first-child,
.ai-bubble ::v-deep h5:first-child,
.ai-bubble ::v-deep h6:first-child {
  margin-top: 0;
}

.ai-bubble ::v-deep h1 { font-size: 1.4em; }
.ai-bubble ::v-deep h2 { font-size: 1.3em; }
.ai-bubble ::v-deep h3 { font-size: 1.2em; }
.ai-bubble ::v-deep h4 { font-size: 1.1em; }
.ai-bubble ::v-deep h5 { font-size: 1.05em; }
.ai-bubble ::v-deep h6 { font-size: 1em; }

.ai-bubble ::v-deep strong {
  color: #fff;
  font-weight: bold;
}

.ai-bubble ::v-deep em {
  color: #fff;
  font-style: italic;
}

.ai-bubble ::v-deep ul,
.ai-bubble ::v-deep ol {
  padding-left: 0.6em;
  margin: 0.15em 0;
}

.ai-bubble ::v-deep li {
  color: #fff;
  margin: 0.1em 0;
}

.ai-bubble ::v-deep pre {
  background-color: rgba(0, 0, 0, 0.3);
  padding: 0.25em;
  border-radius: 0.15em;
  overflow-x: auto;
  margin: 0.15em 0;
  white-space: pre-wrap;
  word-break: normal;
}

.ai-bubble ::v-deep pre code {
  background-color: transparent;
  padding: 0;
  border-radius: 0;
  font-size: 0.85em;
}

.ai-bubble ::v-deep code {
  font-family: 'Courier New', Courier, monospace;
  background-color: rgba(255, 255, 255, 0.15);
  padding: 0.05em 0.15em;
  border-radius: 0.1em;
  font-size: 0.85em;
  word-break: break-word;
}

.ai-bubble ::v-deep blockquote {
  border-left: 0.08em solid rgba(255, 255, 255, 0.5);
  padding: 0.1em 0.3em;
  margin: 0.15em 0;
  color: rgba(255, 255, 255, 0.85);
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 0 0.1em 0.1em 0;
}

.ai-bubble ::v-deep a {
  color: #6cb4ee;
  text-decoration: underline;
}

.ai-bubble ::v-deep a:hover {
  color: #9ad0ff;
}

.streaming-cursor {
  display: inline-block;
  animation: blink 0.6s step-end infinite;
  margin-left: 2px;
  font-weight: bold;
}

@keyframes blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
}

.user-bubble {
  align-self: flex-end;
  background-color: rgba(24, 144, 255, 0.8);
  color: #fff;
}

.chat-input-btn {
  position: absolute;
  top: 50%;
  right: 20px;
  transform: translateY(-50%);
  z-index: 30;
  width: 160px;
  height: 160px;
  border-radius: 50%;
  background-color: rgba(24, 144, 255, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
  transition: transform 0.2s;
}

.chat-input-btn:active {
  transform: translateY(-50%) scale(0.9);
  background-color: rgba(24, 144, 255, 1);
}

.chat-input-btn-icon {
  font-size: 64px;
  line-height: 1;
}

.input-dialog-mask {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 40;
  background-color: rgba(0, 0, 0, 0.3);
  display: flex;
  justify-content: center;
  align-items: center;
}

.input-dialog {
  width: 90%;
  min-width: 600px;
  background-color: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-radius: 40px;
  padding: 36px;
  box-sizing: border-box;
  animation: fadeIn 0.2s ease-in;
}

.input-dialog-container {
  display: flex;
  gap: 24px;
  width: 100%;
}

.input-dialog-container input {
  flex: 1;
  padding: 28px 36px;
  border: none;
  border-radius: 48px;
  background-color: rgba(0, 0, 0, 0.06);
  color: #333;
  font-size: 36px;
  outline: none;
}

.input-dialog-container input::placeholder {
  color: rgba(0, 0, 0, 0.35);
}

.input-dialog-container button {
  padding: 28px 56px;
  border: none;
  border-radius: 48px;
  background-color: rgba(24, 144, 255, 0.8);
  color: #fff;
  font-size: 36px;
  cursor: pointer;
  transition: all 0.3s;
  white-space: nowrap;
}

.input-dialog-container button:disabled {
  background-color: rgba(255, 255, 255, 0.3);
  cursor: not-allowed;
}

.input-dialog-container button:hover:not(:disabled) {
  background-color: rgba(24, 144, 255, 1);
}

.toast {
  position: fixed;
  top: 80px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
}

.toast-content {
  background-color: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 16px 32px;
  border-radius: 8px;
  font-size: 22px;
  animation: fadeInOut 3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeInOut {
  0% {
    opacity: 0;
    transform: translateX(-50%) translateY(-20px);
  }
  10% {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
  90% {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
  100% {
    opacity: 0;
    transform: translateX(-50%) translateY(-20px);
  }
}
</style>

<style>
#video-container video {
  object-fit: cover;
  width: 100%;
  height: 100%;
}
</style>
